<div class="route-management">
  <div class="page-header">
    <div>
      <h1>Routes</h1>
      <p class="text-muted">Configure operator services and the ordered stops passengers will recognise.</p>
    </div>
    <div class="header-actions">
      <button class="btn btn-primary" (click)="startCreateRoute()">
        <i class="material-icons">add_road</i>
        New Route
      </button>
    </div>
  </div>

  <div class="save-banner" *ngIf="successMessage">
    <i class="material-icons">check_circle</i>
    {{ successMessage }}
  </div>
  <div class="save-banner error" *ngIf="errorMessage">
    <i class="material-icons">error</i>
    {{ errorMessage }}
  </div>

  <div class="route-form-container" *ngIf="showRouteForm">
    <div class="form-header">
      <h2>{{ isEditingRoute ? 'Edit Route' : 'Create Route' }}</h2>
      <button class="btn-close" (click)="showRouteForm = false">×</button>
    </div>

    <form [formGroup]="routeForm" (ngSubmit)="saveRoute()" class="route-form">
      <div class="form-row">
        <div class="form-group">
          <label for="routeName">Route name *</label>
          <input id="routeName" type="text" formControlName="routeName" class="form-control" placeholder="CBD Express">
          <span class="error" *ngIf="isInvalid('routeName') && !routeNameError">Route name is required.</span>
          <span class="error" *ngIf="routeNameError">{{ routeNameError }}</span>
        </div>

        <div class="form-group">
          <label for="statusID">Status *</label>
          <select id="statusID" formControlName="statusID" class="form-control">
            <option *ngFor="let status of statuses" [ngValue]="status.statusID">{{ status.statusName }}</option>
          </select>
        </div>
      </div>

      <div class="form-row">
        <div class="form-group" *ngIf="isAdminOrPlatform">
          <label for="organisationID">Organisation *</label>
          <select id="organisationID" formControlName="organisationID" class="form-control">
            <option [ngValue]="null">Select organisation</option>
            <option *ngFor="let organisation of organisations" [ngValue]="organisation.organisationID">
              {{ getOrganisationOptionLabel(organisation) }}
            </option>
          </select>
          <span class="error" *ngIf="isInvalid('organisationID')">Organisation is required.</span>
        </div>

        <div class="form-group">
          <label for="description">Description</label>
          <textarea id="description" formControlName="description" class="form-control" rows="3" placeholder="Morning commuter route"></textarea>
        </div>
      </div>

      <div class="form-actions">
        <button type="button" class="btn btn-secondary" (click)="showRouteForm = false">Cancel</button>
        <button type="submit" class="btn btn-primary" [disabled]="isSavingRoute">
          {{ isSavingRoute ? 'Saving...' : 'Save Route' }}
        </button>
      </div>
    </form>
  </div>

  <div class="route-workspace">
    <section class="routes-list">
      <div class="list-header">
        <div>
          <h3>Route List ({{ routes.length }})</h3>
          <p class="text-muted">{{ includeInactive ? 'Active and inactive routes' : 'Active routes only' }}</p>
        </div>
        <div class="filter-controls">
          <select class="form-control filter-select" *ngIf="isAdminOrPlatform" [(ngModel)]="selectedOrganisationId" (ngModelChange)="onOrganisationFilterChange()">
            <option [ngValue]="null">All organisations</option>
            <option *ngFor="let organisation of organisations" [ngValue]="organisation.organisationID">
              {{ getOrganisationOptionLabel(organisation) }}
            </option>
          </select>
          <label class="toggle-control">
            <input type="checkbox" [(ngModel)]="includeInactive" (ngModelChange)="loadRoutes()">
            <span>Include inactive</span>
          </label>
        </div>
      </div>

      <div class="routes-table" *ngIf="routes.length">
        <table>
          <thead>
            <tr>
              <th>Route</th>
              <th *ngIf="isAdminOrPlatform">Organisation</th>
              <th>Status</th>
              <th>Stops</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let route of routes" [class.inactive-row]="isInactive(route)" [class.selected-row]="selectedRoute?.routeID === route.routeID" (click)="selectRoute(route)">
              <td>
                <strong>{{ route.routeName }}</strong>
                <p class="text-muted">{{ getRouteDescription(route) }}</p>
              </td>
              <td *ngIf="isAdminOrPlatform">{{ route.organisationName || route.organisationID }}</td>
              <td>
                <span class="badge" [class.badge-success]="!isInactive(route)">{{ route.statusName || (isInactive(route) ? 'Inactive' : 'Active') }}</span>
                <small class="inactive-note" *ngIf="isInactive(route)">Not available for new trip assignment</small>
              </td>
              <td>{{ (route.stops || route.routeStops || []).length }} stops</td>
              <td class="actions-cell" (click)="$event.stopPropagation()">
                <button class="btn-icon" title="View route" (click)="selectRoute(route)">
                  <i class="material-icons">visibility</i>
                </button>
                <button class="btn-icon" title="Edit route" (click)="startEditRoute(route)">
                  <i class="material-icons">edit</i>
                </button>
                <button class="btn-icon" title="Duplicate route with stops" (click)="duplicateRoute(route)">
                  <i class="material-icons">content_copy</i>
                </button>
                <button class="btn-icon btn-danger" title="Archive route" (click)="archiveRoute(route)" [disabled]="isInactive(route)">
                  <i class="material-icons">archive</i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="empty-state" *ngIf="!routes.length && !isLoading">
        <i class="material-icons">route</i>
        <h3>No routes found</h3>
        <p>Create an active route before scheduling trips.</p>
        <button class="btn btn-primary" (click)="startCreateRoute()">Create Route</button>
      </div>
    </section>

    <section class="route-detail-panel" *ngIf="selectedRoute">
      <div class="list-header">
        <div>
          <h3>{{ selectedRoute.routeName }}</h3>
          <p class="text-muted">{{ getRouteDescription(selectedRoute) }}</p>
        </div>
        <div class="header-actions">
          <span class="badge" [class.badge-success]="!isInactive(selectedRoute)">
            {{ selectedRoute.statusName || (isInactive(selectedRoute) ? 'Inactive' : 'Active') }}
          </span>
          <button class="btn btn-secondary" (click)="startEditRoute(selectedRoute)">
            <i class="material-icons">edit</i>
            Edit
          </button>
          <button class="btn btn-secondary" (click)="duplicateRoute(selectedRoute)">
            <i class="material-icons">content_copy</i>
            Duplicate
          </button>
          <button class="btn btn-secondary" (click)="startUseSavedStop()" [disabled]="isInactive(selectedRoute)">
            <i class="material-icons">bookmark_add</i>
            Use Saved Stop
          </button>
          <button class="btn btn-primary" (click)="startCreateStop()" [disabled]="isInactive(selectedRoute)">
            <i class="material-icons">add_location</i>
            Add Stop
          </button>
        </div>
      </div>

      <div class="inactive-callout" *ngIf="isInactive(selectedRoute)">
        <i class="material-icons">info</i>
        This route is inactive. It is visible for review, but should not be assigned to new trips.
      </div>

      <section class="route-form stop-editor" *ngIf="showSavedStopPicker">
        <div class="form-header inline-header">
          <div><h2>Use Saved Stop</h2><p class="text-muted">Reuse a place already saved for this operator.</p></div>
          <button type="button" class="btn-close" (click)="showSavedStopPicker = false">×</button>
        </div>
        <div class="form-row">
          <div class="form-group">
            <label for="savedStop">Saved stop *</label>
            <select id="savedStop" class="form-control" [(ngModel)]="selectedSavedStopId">
              <option [ngValue]="null">Select a saved stop</option>
              <option *ngFor="let stop of savedStops" [ngValue]="stop.savedStopID">{{ getSavedStopLabel(stop) }}</option>
            </select>
            <small class="text-muted" *ngIf="!savedStops.length">No saved stops exist for this operator yet.</small>
          </div>
          <div class="form-group">
            <label for="savedStopOrder">Stop order *</label>
            <input id="savedStopOrder" class="form-control" type="number" min="1" [(ngModel)]="savedStopOrder">
            <span class="error" *ngIf="stopOrderError">{{ stopOrderError }}</span>
          </div>
        </div>
        <div class="form-actions">
          <button type="button" class="btn btn-secondary" (click)="startCreateSavedStop()"><i class="material-icons">add_location_alt</i> Save New Place</button>
          <button type="button" class="btn btn-primary" (click)="addSavedStopToRoute()" [disabled]="isSavingSavedStop || !selectedSavedStopId">{{ isSavingSavedStop ? 'Adding...' : 'Add to Route' }}</button>
        </div>
      </section>

      <form [formGroup]="savedStopForm" (ngSubmit)="saveSavedStop()" class="route-form stop-editor" *ngIf="showSavedStopForm">
        <div class="form-header inline-header">
          <div><h2>Save Reusable Place</h2><p class="text-muted">Saved places can be added to other routes without recapturing coordinates.</p></div>
          <button type="button" class="btn-close" (click)="showSavedStopForm = false">×</button>
        </div>
        <div class="form-row">
          <div class="form-group">
            <label for="savedStopName">Stop name *</label>
            <input id="savedStopName" class="form-control uppercase-input" formControlName="stopName" placeholder="MAIN STATION">
            <span class="error" *ngIf="isSavedStopInvalid('stopName')">Stop name is required.</span>
          </div>
          <div class="form-group">
            <label for="savedStreet">Street address</label>
            <input id="savedStreet" class="form-control" formControlName="streetAddress" placeholder="1 Main Street">
          </div>
        </div>
        <div class="form-row">
          <div class="form-group"><label for="savedLatitude">Latitude *</label><input id="savedLatitude" class="form-control" type="number" min="-90" max="90" step="0.000001" formControlName="stopLatitude"><span class="error" *ngIf="isSavedStopInvalid('stopLatitude')">Enter a latitude between -90 and 90.</span></div>
          <div class="form-group"><label for="savedLongitude">Longitude *</label><input id="savedLongitude" class="form-control" type="number" min="-180" max="180" step="0.000001" formControlName="stopLongitude"><span class="error" *ngIf="isSavedStopInvalid('stopLongitude')">Enter a longitude between -180 and 180.</span></div>
        </div>
        <div class="address-grid">
          <div class="form-group"><label for="savedSuburb">Suburb</label><input id="savedSuburb" class="form-control" formControlName="suburb"></div>
          <div class="form-group"><label for="savedCity">City</label><input id="savedCity" class="form-control" formControlName="city"></div>
          <div class="form-group"><label for="savedProvince">Province</label><input id="savedProvince" class="form-control" formControlName="province"></div>
          <div class="form-group"><label for="savedPostalCode">Postal code</label><input id="savedPostalCode" class="form-control" formControlName="postalCode"></div>
          <div class="form-group"><label for="savedCountry">Country</label><input id="savedCountry" class="form-control" formControlName="country"></div>
        </div>
        <div class="form-actions">
          <button type="button" class="btn btn-secondary" (click)="startUseSavedStop()">Cancel</button>
          <button type="submit" class="btn btn-primary" [disabled]="isSavingSavedStop">{{ isSavingSavedStop ? 'Saving...' : 'Save Place' }}</button>
        </div>
      </form>

      <form [formGroup]="stopForm" (ngSubmit)="saveStop()" class="route-form stop-editor" *ngIf="showStopForm">
        <div class="form-header inline-header">
          <h2>{{ isEditingStop ? 'Edit Stop' : 'Add Stop' }}</h2>
          <button type="button" class="btn-close" (click)="showStopForm = false">×</button>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label for="stopName">Stop name *</label>
            <input id="stopName" type="text" formControlName="stopName" class="form-control uppercase-input" placeholder="MAIN STATION">
            <span class="error" *ngIf="isInvalid('stopName', stopForm)">Stop name is required.</span>
          </div>
          <div class="form-group">
            <label for="stopOrder">Stop order *</label>
            <input id="stopOrder" type="number" formControlName="stopOrder" class="form-control" min="1">
            <span class="error" *ngIf="isInvalid('stopOrder', stopForm) && !stopOrderError">Stop order must be greater than zero.</span>
            <span class="error" *ngIf="stopOrderError">{{ stopOrderError }}</span>
          </div>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label for="stopLatitude">Latitude *</label>
            <input id="stopLatitude" type="number" formControlName="stopLatitude" class="form-control" min="-90" max="90" step="0.000001">
            <span class="error" *ngIf="isInvalid('stopLatitude', stopForm)">Latitude must be between -90 and 90.</span>
          </div>
          <div class="form-group">
            <label for="stopLongitude">Longitude *</label>
            <input id="stopLongitude" type="number" formControlName="stopLongitude" class="form-control" min="-180" max="180" step="0.000001">
            <span class="error" *ngIf="isInvalid('stopLongitude', stopForm)">Longitude must be between -180 and 180.</span>
          </div>
        </div>

        <label class="toggle-control reuse-toggle" *ngIf="!isEditingStop">
          <input type="checkbox" formControlName="saveForReuse">
          <span>Save this place for reuse on other routes</span>
        </label>
        <small class="text-muted" *ngIf="!isEditingStop && stopForm.get('saveForReuse')?.value">
          The stop will be added to this route and to the operator's saved-stop catalogue.
        </small>

        <label class="toggle-control">
          <input type="checkbox" formControlName="hasAddress" (change)="onAddressToggle()">
          <span>Capture address</span>
        </label>

        <div class="address-grid" formGroupName="address" *ngIf="stopForm.get('hasAddress')?.value">
          <div class="form-group">
            <label for="streetAddress">Street *</label>
            <input id="streetAddress" type="text" formControlName="streetAddress" class="form-control" placeholder="1 Main Street">
            <span class="error" *ngIf="isAddressInvalid('streetAddress')">Street is required when an address is supplied.</span>
          </div>
          <div class="form-group">
            <label for="suburb">Suburb</label>
            <input id="suburb" type="text" formControlName="suburb" class="form-control" placeholder="Johannesburg Central">
          </div>
          <div class="form-group">
            <label for="city">City *</label>
            <input id="city" type="text" formControlName="city" class="form-control" placeholder="Johannesburg">
            <span class="error" *ngIf="isAddressInvalid('city')">City is required when an address is supplied.</span>
          </div>
          <div class="form-group">
            <label for="province">Province</label>
            <input id="province" type="text" formControlName="province" class="form-control" placeholder="Gauteng">
          </div>
          <div class="form-group">
            <label for="postalCode">Postal code</label>
            <input id="postalCode" type="text" formControlName="postalCode" class="form-control" placeholder="2001">
          </div>
          <div class="form-group">
            <label for="country">Country *</label>
            <input id="country" type="text" formControlName="country" class="form-control" placeholder="South Africa">
            <span class="error" *ngIf="isAddressInvalid('country')">Country is required when an address is supplied.</span>
          </div>
        </div>

        <div class="form-actions">
          <button type="button" class="btn btn-secondary" (click)="showStopForm = false">Cancel</button>
          <button type="submit" class="btn btn-primary" [disabled]="isSavingStop">
            {{ isSavingStop ? 'Saving...' : 'Save Stop' }}
          </button>
        </div>
      </form>

      <div class="stops-list">
        <article class="stop-row" *ngFor="let stop of selectedRouteStops">
          <div class="stop-order">{{ stop.stopOrder }}</div>
          <div class="stop-body">
            <strong>{{ stop.stopName }}</strong>
            <small *ngIf="stop.savedStopID">Saved place</small>
            <span>{{ stop.stopLatitude }}, {{ stop.stopLongitude }}</span>
            <small>{{ getStopAddress(stop) }}</small>
          </div>
          <div class="actions-cell">
            <button class="btn-icon" type="button" title="Save stop for reuse" *ngIf="!stop.savedStopID" (click)="saveRouteStopForReuse(stop)" [disabled]="isSavingSavedStop">
              <i class="material-icons">bookmark_add</i>
            </button>
            <button class="btn-icon" title="Edit stop" (click)="startEditStop(stop)">
              <i class="material-icons">edit_location_alt</i>
            </button>
            <button class="btn-icon btn-danger" title="Delete stop" (click)="deleteStop(stop)">
              <i class="material-icons">delete</i>
            </button>
          </div>
        </article>

        <div class="empty-state compact" *ngIf="!selectedRouteStops.length">
          <i class="material-icons">pin_drop</i>
          <h3>No stops yet</h3>
          <p>Add the ordered points passengers will search for.</p>
        </div>
      </div>
    </section>
  </div>
</div>
